import {atlasMapState} from '@/services/state/map';

import type {INotificationHandler} from '../types';
import {featureCache} from '@/services/cache/feature';

export const createOpenObjectHandler = (): INotificationHandler => {
  const canHandle = (action: string): boolean => {
    return action === 'open_object';
  };

  const handle = async (message: Record<string, unknown>): Promise<boolean> => {
    const params = message.params as Record<string, unknown> | undefined;
    const objectId = params?.object_id as string | undefined;

    console.log('[Notif] openObjectHandler called', {
      objectId,
      params,
      message,
    });

    if (!objectId) {
      console.warn('[Notif] object_id missing in params', {params, message});
      return false;
    }

    const allFeatures = featureCache.getCache.value;
    const layerIds = Object.keys(allFeatures);

    console.log('[Notif] Feature cache state', {
      layerIds,
      layerCount: layerIds.length,
      featuresPerLayer: Object.fromEntries(
        layerIds.map((id) => [id, allFeatures[id]?.length ?? 0]),
      ),
    });

    for (const [layerId, features] of Object.entries(allFeatures)) {
      const foundFeature = features.find(
        (feature) =>
          (feature.properties as Record<string, unknown>).__id__ === objectId,
      );

      if (foundFeature) {
        const leafletLayer = atlasMapState.actions.getFeature(
          layerId,
          objectId,
        );

        console.log('[Notif] Feature found in cache', {
          objectId,
          layerId,
          hasLeafletLayer: !!leafletLayer,
          featureProperties: foundFeature.properties,
        });

        atlasMapState.actions.openPopup(layerId, objectId);

        const leafletLayerAfter = atlasMapState.actions.getFeature(
          layerId,
          objectId,
        );

        console.log('[Notif] After openPopup', {
          objectId,
          layerId,
          hasLeafletLayerAfter: !!leafletLayerAfter,
          wasAlreadyOnMap: !!leafletLayer,
          queued: !leafletLayer && !leafletLayerAfter,
        });

        return true;
      }
    }

    const allCachedIds = layerIds.flatMap((id) =>
      (allFeatures[id] ?? [])
        .map((f) => (f.properties as Record<string, unknown>).__id__ as string)
        .filter(Boolean),
    );

    console.warn('[Notif] Feature not found in any layer', {
      objectId,
      layerIds,
      allCachedIds,
      exactMatch: allCachedIds.filter((id) => id === objectId),
      partialMatches: allCachedIds.filter(
        (id) => id?.includes(objectId) || objectId.includes(id ?? ''),
      ),
    });

    return false;
  };

  return {canHandle, handle};
};
